import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { CheckCircle, Loader2, Eraser, PenTool } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const JumboOwnerSignPublicForm = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [hasSignature, setHasSignature] = useState(false);

  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);

  // Load owner context
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/shared/jumbo-owner-sign/${token}`)
      .then(res => {
        setInfo(res.data);
        const o = res.data?.owner;
        if (o) setSignedName(`${o.first_name || ''} ${o.last_name || ''}`.trim());
        setLoading(false);
      })
      .catch(() => { setError('This signature link is invalid or has expired.'); setLoading(false); });
  }, [token]);

  // Resize canvas crisp on hi-dpi
  useEffect(() => {
    if (loading || error) return;
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0E333F';
    ctx.lineWidth = 2.4;
  }, [loading, error]);

  const getPoint = (evt) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    if (evt.touches && evt.touches.length) {
      return {
        x: evt.touches[0].clientX - rect.left,
        y: evt.touches[0].clientY - rect.top,
      };
    }
    return { x: evt.clientX - rect.left, y: evt.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = getPoint(e);
  };

  const moveDraw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const pt = getPoint(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPointRef.current = pt;
    if (!hasSignature) setHasSignature(true);
  };

  const endDraw = () => { drawingRef.current = false; };

  const clearSignature = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    setHasSignature(false);
  };

  const getSignatureDataUrl = () => {
    const c = canvasRef.current;
    if (!c) return null;
    // Convert to PNG with transparent background
    return c.toDataURL('image/png');
  };

  const handleSubmit = async () => {
    if (!hasSignature) { alert('Please draw your signature above before submitting.'); return; }
    if (!consent) { alert('You must agree to electronically sign this document.'); return; }
    if (!signedName.trim()) { alert('Please type your full legal name.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/shared/jumbo-owner-sign/${token}`, {
        signature_data_url: getSignatureDataUrl(),
        signed_name: signedName.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === 'string'
        ? d
        : Array.isArray(d) ? d.map(x => x?.msg || JSON.stringify(x)).join('; ')
        : 'Submission failed. Please try again.';
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
    </div>
  );
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="bg-white border border-red-200 rounded-xl p-8 max-w-md text-center">
        <p className="text-red-700 font-semibold">{error}</p>
      </div>
    </div>
  );
  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="bg-white border border-emerald-200 rounded-xl p-10 max-w-md text-center">
        <CheckCircle className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-800">Signature received</h2>
        <p className="text-slate-600 mt-2">The signed application has been delivered to the broker. You may close this window.</p>
      </div>
    </div>
  );

  const ownerName = info?.owner ? `${info.owner.first_name || ''} ${info.owner.last_name || ''}`.trim() : '';
  const pdfUrl = `${BACKEND_URL}/api/shared/jumbo-owner-sign/${token}/pdf`;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="jumbo-owner-sign-page">
      <div className="max-w-5xl mx-auto">
        <div className="bg-[#081120] text-white rounded-t-xl px-6 py-5">
          <h1 className="text-xl font-bold">Quidity Direct — Sign Application</h1>
          <p className="text-sm text-slate-300 mt-1">
            {ownerName ? <><strong>{ownerName}</strong> · </> : null}
            Application for <strong>{info?.business_legal_name || 'the business'}</strong>
          </p>
        </div>
        <div className="bg-white border border-slate-200 border-t-0 rounded-b-xl p-6 space-y-5">
          {/* PDF preview */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-slate-700">Application Preview</h3>
              <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-xs text-[#0E333F] hover:underline" data-testid="sign-page-open-pdf">
                Open in new tab
              </a>
            </div>
            <iframe
              title="Application Preview"
              src={pdfUrl}
              className="w-full h-[60vh] border border-slate-200 rounded-lg bg-slate-100"
              data-testid="sign-page-pdf-iframe"
            />
          </div>

          {/* Signature pad */}
          <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                <PenTool className="h-4 w-4" /> Draw your signature
              </h3>
              <Button size="sm" variant="outline" onClick={clearSignature} data-testid="signature-clear-btn">
                <Eraser className="h-3.5 w-3.5 mr-1" /> Clear
              </Button>
            </div>
            <div className="relative bg-white border-2 border-dashed border-slate-300 rounded-lg" style={{ height: 180 }}>
              <canvas
                ref={canvasRef}
                className="w-full h-full touch-none rounded-lg"
                onMouseDown={startDraw}
                onMouseMove={moveDraw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={moveDraw}
                onTouchEnd={endDraw}
                data-testid="signature-canvas"
              />
              {!hasSignature && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="text-slate-300 text-sm italic">Sign here using your finger or mouse</span>
                </div>
              )}
            </div>
          </div>

          {/* Typed name + consent */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="sign-name" className="text-xs uppercase font-semibold text-slate-700">Your Full Legal Name *</Label>
              <input
                id="sign-name"
                type="text"
                value={signedName}
                onChange={e => setSignedName(e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300"
                data-testid="signed-name-input"
              />
            </div>
          </div>

          <label className="flex items-start gap-3 text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <Checkbox
              checked={consent}
              onCheckedChange={(v) => setConsent(!!v)}
              data-testid="esign-consent-checkbox"
              className="mt-0.5"
            />
            <span>
              I, the named applicant above, agree that my drawn signature and typed name constitute my legal electronic signature on this Quidity Direct credit application. I have reviewed the application above and confirm the information is accurate.
            </span>
          </label>

          <div className="pt-2 border-t border-slate-200 flex justify-end">
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#0E333F] hover:bg-[#072028] text-white"
              data-testid="sign-submit-btn"
            >
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing…</> : 'Submit Signed Application'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JumboOwnerSignPublicForm;
